import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { 
  ArrowLeft,
  Crown,
  Users,
  Search,
  Calendar,
  Mail,
  FileText,
  UserCheck,
  LogOut,
  Activity,
  ExternalLink,
  User,
  ChevronDown
} from 'lucide-react';
import { formatDate } from '../utils/dateFormatter';

// Using relative URLs
const API = '/api';

const SuperAdminCustomers = () => {
  const { user, emulateCustomer, logout } = useAuth();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [emulatingUser, setEmulatingUser] = useState(null);

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    filterAndSortCustomers();
  }, [customers, searchTerm, sortBy]);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/admin/customers`);
      setCustomers(response.data);
    } catch (error) {
      toast.error('Failed to fetch customers');
      console.error('Customers fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortCustomers = () => {
    let filtered = [...customers];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(customer =>
        customer.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'email':
          return a.email.localeCompare(b.email);
        case 'audit_count':
          return (b.audit_count || 0) - (a.audit_count || 0);
        case 'last_login':
          const aLogin = new Date(a.last_login || 0);
          const bLogin = new Date(b.last_login || 0);
          return bLogin - aLogin;
        case 'created_at':
        default:
          return new Date(b.created_at) - new Date(a.created_at);
      }
    });

    setFilteredCustomers(filtered);
  };

  const handleEmulateCustomer = async (customerId, customerEmail, workspaceId) => {
    const rowKey = `${customerId}-${workspaceId}`;
    setEmulatingUser(rowKey);
    try {
      await emulateCustomer(customerId, workspaceId);
      // emulateCustomer updates auth state and navigates client-side (no reload)
      toast.success(`Now emulating ${customerEmail}`);
    } catch (error) {
      toast.error('Failed to emulate customer');
      setEmulatingUser(null);
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      'account_owner': 'bg-emerald-100 text-navy-700 border-emerald-200',
      'member': 'bg-blue-100 text-blue-700 border-blue-200',
      'auditor': 'bg-purple-100 text-purple-700 border-purple-200'
    };
    return colors[role] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-appbg">
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-appbg">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-xl border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/admin')}
                className="flex items-center space-x-2"
                data-testid="back-to-admin"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Admin Dashboard</span>
              </Button>
              
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">All Customers</h1>
                  <p className="text-sm text-slate-600">View and emulate customer accounts</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <Badge className="bg-purple-100 text-purple-700 border-purple-200 hover:bg-purple-100 hover:!text-purple-700">
                <Crown className="w-3 h-3 mr-1" />
                Super Admin
              </Badge>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="flex items-center gap-2 border-slate-200 hover:bg-slate-50 hover:text-slate-900"
                  >
                    <User className="w-4 h-4 text-slate-600" />
                    <span className="text-sm font-medium text-slate-900 max-w-[150px] truncate">
                      {user?.full_name || user?.email?.split('@')[0]}
                    </span>
                    <ChevronDown className="w-4 h-4 text-slate-500" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium text-slate-900">{user?.email}</p>
                      <p className="text-xs text-slate-500">Super Administrator</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={logout}
                    className="text-red-600 focus:text-red-600 cursor-pointer"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8 animate-fadeIn">
          <Card className="border-0 shadow-soft bg-white/80 backdrop-blur-sm">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <p className="text-2xl font-bold text-slate-900">{customers.length}</p>
              <p className="text-sm text-slate-600">Total Customers</p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-soft bg-white/80 backdrop-blur-sm">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Activity className="w-6 h-6 text-navy-600" />
              </div>
              <p className="text-2xl font-bold text-navy-600">
                {customers.filter(c => c.last_login).length}
              </p>
              <p className="text-sm text-slate-600">Active Users</p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-soft bg-white/80 backdrop-blur-sm">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <FileText className="w-6 h-6 text-orange-600" />
              </div>
              <p className="text-2xl font-bold text-orange-600">
                {customers.reduce((sum, c) => sum + (c.audit_count || 0), 0)}
              </p>
              <p className="text-sm text-slate-600">Total Audits</p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-soft bg-white/80 backdrop-blur-sm">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <UserCheck className="w-6 h-6 text-purple-600" />
              </div>
              <p className="text-2xl font-bold text-purple-600">
                {Math.round((customers.filter(c => c.audit_count > 0).length / customers.length) * 100) || 0}%
              </p>
              <p className="text-sm text-slate-600">Adoption Rate</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="mb-6 animate-slideIn">
          <Card className="border-0 shadow-soft bg-white/80 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center space-x-2">
                  <Search className="w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search by email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-64"
                    data-testid="search-customers"
                  />
                </div>
                
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-48" data-testid="sort-customers">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="created_at">Recently Joined</SelectItem>
                    <SelectItem value="last_login">Last Login</SelectItem>
                    <SelectItem value="email">Email (A-Z)</SelectItem>
                    <SelectItem value="audit_count">Most Audits</SelectItem>
                  </SelectContent>
                </Select>

                <div className="flex items-center space-x-2 text-sm text-slate-600">
                  <span>Showing {filteredCustomers.length} of {customers.length} customers</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Customers Table */}
        <Card className="border-0 shadow-soft bg-white/80 backdrop-blur-sm animate-slideIn">
          <CardHeader>
            <CardTitle>All Customers</CardTitle>
            <CardDescription>Manage customer accounts and emulate their experience</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left p-4 font-semibold text-slate-700">Customer</th>
                    <th className="text-left p-4 font-semibold text-slate-700">Role</th>
                    <th className="text-left p-4 font-semibold text-slate-700">Audits</th>
                    <th className="text-left p-4 font-semibold text-slate-700">Joined</th>
                    <th className="text-left p-4 font-semibold text-slate-700">Last Login</th>
                    <th className="text-left p-4 font-semibold text-slate-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCustomers.map((customer, index) => (
                    <tr 
                      key={`${customer.id}-${customer.workspace_id}`} 
                      className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${
                        index % 2 === 0 ? 'bg-white' : 'bg-slate-25'
                      }`}
                      data-testid={`customer-row-${customer.id}-${customer.workspace_id}`}
                    >
                      <td className="p-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                            <Mail className="w-5 h-5 text-slate-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{customer.email}</p>
                            <p className="text-sm text-navy-600">{customer.workspace_name}</p>
                            <p className="text-xs text-slate-500">ID: {customer.id.slice(0, 8)}...</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <Badge 
                          variant="outline"
                          className="bg-blue-50 text-blue-700 border-blue-200 text-xs"
                        >
                          Owner
                        </Badge>
                      </td>
                      <td className="p-4">
                        <div className="text-center">
                          <p className="text-lg font-semibold text-slate-900">{customer.audit_count || 0}</p>
                          <p className="text-xs text-slate-500">audits</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center space-x-2 text-sm text-slate-600">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(customer.created_at)}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="text-sm text-slate-600">
                          {formatDate(customer.last_login)}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEmulateCustomer(customer.id, customer.email, customer.workspace_id)}
                            disabled={emulatingUser === `${customer.id}-${customer.workspace_id}`}
                            className="flex items-center space-x-1 text-xs hover:bg-emerald-50 hover:border-emerald-200 hover:text-navy-700"
                            data-testid={`emulate-${customer.id}-${customer.workspace_id}`}
                          >
                            {emulatingUser === `${customer.id}-${customer.workspace_id}` ? (
                              <>
                                <div className="w-3 h-3 border-2 border-navy-600/30 border-t-emerald-600 rounded-full animate-spin" />
                                <span>Emulating...</span>
                              </>
                            ) : (
                              <>
                                <ExternalLink className="w-3 h-3" />
                                <span>Emulate</span>
                              </>
                            )}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredCustomers.length === 0 && (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <h4 className="text-lg font-medium text-slate-900 mb-2">No customers found</h4>
                <p className="text-slate-600">
                  {customers.length === 0 
                    ? 'No customers have signed up yet.'
                    : 'Try adjusting your search or filter criteria.'
                  }
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SuperAdminCustomers;